import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { CSTooltip } from 'components/Tooltip/CSToolTip';
import { QuestionCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { DEFAULT_TC_TEXT, isApplyToConfidence } from 'helpers/offer.helpers';
import { Button } from 'components/form/Button/Button';
import { PredictedCreditLimit } from 'components/Offers/StandardCreditCardOffer/StandardCreditCardOffer';
import { CreditSavings } from 'components/Offers/StandardCreditCardOffer/StandardCreditCardOffer';
import Label from 'components/form/Label/Label';
import './CarouselCreditCardOffer.scss';
import { ReactComponent as LockIcon } from 'style/_assets/icons/lock-white.svg';
import { FlexContainer } from 'components/Containers/FlexContainer/FlexContainer';
import { ReactComponent as Checkmark } from 'style/icons/checkmark.svg';
import BonusText from '../fragments/BonusText/BonusText';
import { Tag } from 'components/Tag';
import MinimunDeposit from './MinimunDeposit';
import RegularAPR from './RegularAPR';
import AnnualFee from './AnnualFee';
import { CREDIT_CARD_VERTICAL } from 'types/marketplaceTiles.constants';
import ApprovalOddsBadge from 'components/Offers/fragments/ApprovalOddsBadge';
import ReviewsRating from 'components/Offers/fragments/ReviewsRating';
import SesameGuarantee from 'components/Offers/fragments/SesameGuarantee';
import { GUARANTEE } from 'helpers/approvalOdds.helper';
import { PrePopulatedTooltip } from '../fragments/PrePopulatedTooltip/PrePopulatedTooltip';
import { Link } from 'components/navigation/Link/Link';
import { useConfig } from 'hook/config.hook';
import AmexWithConfidence from '../AmexWithConfidence/AmexWithConfidence';

const Prepopulate = ({ datatestid, prePopulatedTooltipText }) => {
  if (prePopulatedTooltipText) {
    return (
      <div
        className="prepopulate-tooltip"
        data-testid={datatestid ? `${datatestid}-prepopulate-tooltip` : null}
      >
        <PrePopulatedTooltip
          prePopulatedTooltipText={prePopulatedTooltipText}
        />
      </div>
    );
  } else {
    return <div data-testid="prepropulate-spacer" style={{ height: 27 }}></div>;
  }
};

const CreditCardTopper = props => {
  const {
    offerMetadata,
    showCreditSavings,
    hideCreditLimit,
    showCheckbox,
    customTopper,
  } = props;
  if (showCreditSavings) {
    return (
      <>
        <CreditSavings
          creditSavings={offerMetadata.creditSavings}
          datatestid={'standardWideOffer'}
        />
        {customTopper && <props.customTopper {...props} />}
      </>
    );
  } else if (!hideCreditLimit) {
    return (
      <FlexContainer
        className={classnames('width100', {
          'align-baseline justify-around': props.showCheckbox,
        })}
      >
        <PredictedCreditLimit
          datatestid={'standardWideOffer'}
          predictedCreditLimit={offerMetadata.predictedCreditLimit}
        />
        {customTopper && <props.customTopper {...props} />}
      </FlexContainer>
    );
  } else if (showCheckbox) {
    return (
      <FlexContainer className="width100 justify-end margR8">
        {customTopper && <props.customTopper {...props} />}
      </FlexContainer>
    );
  } else {
    return '';
  }
};

const CCTopperOutsideContainer = props => {
  const { customTopperOutsideContainer } = props;
  if (customTopperOutsideContainer) {
    return <props.customTopperOutsideContainer {...props} />;
  }
  return <></>;
};

const BodyContainer = ({
  containerType,
  bodyContentType,
  hideNAFields,
  minimunDeposit,
  ...props
}) => {
  const {
    introBtOverrideText,
    introPurchaseOverrideText,
    rewardsBonusTextShort,
    rewardsBonusTextLong,
    signupBonusTextShort,
    signupBonusTextLong,
    aprDetails,
    annualFeeDetails,
  } = props.offerMetadata;

  const showMinimunDepositLabel = minimunDeposit?.minimunDeposit;
  const getIntroBalance = () => {
    return introBtOverrideText ? introBtOverrideText : 'N/A';
  };
  const getIntroPurchase = () => {
    return introPurchaseOverrideText ? introPurchaseOverrideText : 'N/A';
  };
  const hideIntroBalanceAprNA = hideNAFields && getIntroBalance() === 'N/A';
  const hideIntroPurchaseAprNA = hideNAFields && getIntroPurchase() === 'N/A';
  if (bodyContentType === 'BALANCE_TRANSFER') {
    return (
      <div className="offerview-body-lg-container">
        {!hideIntroBalanceAprNA && (
          <div className="introPurchaseBlock">
            <div className="introPurchaseBlockTitle">
              Intro Balance Transfer APR
            </div>
            <div className="introPurchase">{getIntroBalance()}</div>
          </div>
        )}
        {!hideIntroPurchaseAprNA && (
          <div className="introBalanceBlock">
            <div className="introBalanceBlockTitle">Intro Purchase APR</div>
            <div className="introBalance">{getIntroPurchase()}</div>
          </div>
        )}
        <RegularAPR aprDetails={aprDetails} />
        <AnnualFee annualFeeDetails={annualFeeDetails} />
      </div>
    );
  } else if (
    containerType === 'SUGGESTED_REWARDS_CARDS' ||
    containerType === 'OCF_REWARDS_CARDS' ||
    containerType === 'AIRLINE_CARDS' ||
    containerType === 'CASH_BACK_CARDS' ||
    bodyContentType === 'REWARDS'
  ) {
    return (
      <div className="offerview-body-lg-container">
        <BonusText
          type={'Rewards'}
          bonusTextLong={rewardsBonusTextLong}
          bonusTextShort={rewardsBonusTextShort}
          tooltipIcon={InfoCircleOutlined}
        />
        <BonusText
          type={'Bonus Offer'}
          bonusTextLong={signupBonusTextLong}
          bonusTextShort={signupBonusTextShort}
          tooltipIcon={InfoCircleOutlined}
        />
        <RegularAPR aprDetails={aprDetails} />
        <AnnualFee annualFeeDetails={annualFeeDetails} />
      </div>
    );
  } else if (
    containerType === 'SUGGESTED_BALANCE_TRANSFER_CARDS' ||
    containerType === 'OCF_BT_CARDS' ||
    bodyContentType === 'BALANCE_TRANSFER'
  ) {
    return (
      <div className="offerview-body-lg-container">
        {!hideIntroBalanceAprNA && (
          <div className="introPurchaseBlock">
            <div className="introPurchaseBlockTitle">
              Intro Balance Transfer APR
            </div>
            <div className="introPurchase">{getIntroBalance()}</div>
          </div>
        )}
        {!hideIntroPurchaseAprNA && (
          <div className="introBalanceBlock">
            <div className="introBalanceBlockTitle">Intro Purchase APR</div>
            <div className="introBalance">{getIntroPurchase()}</div>
          </div>
        )}
        <RegularAPR aprDetails={aprDetails} />
        <AnnualFee annualFeeDetails={annualFeeDetails} />
      </div>
    );
  } else {
    return (
      <div className="offerview-body-sm-container">
        <RegularAPR aprDetails={aprDetails} />
        <AnnualFee annualFeeDetails={annualFeeDetails} />
        {showMinimunDepositLabel && (
          <MinimunDeposit
            minimunDepositDetails={minimunDeposit?.minimunDeposit}
          />
        )}
      </div>
    );
  }
};

export const PrequalifiedTooltip = ({ datatestid, pagePosition }) => {
  const { config } = useConfig();
  return (
    <CSTooltip
      title={config?.disclaimers?.prequalifiedTooltip}
      isInline={true}
      datatestid={datatestid ? `${datatestid}-tooltip` : null}
      tooltipName={'Prequalified Tooltip'}
      pagePosition={pagePosition}
      removeMargins
      vertical={CREDIT_CARD_VERTICAL}
    >
      <div className="flex-container justify-center align-items-center flex-row noWrap">
        <div className="greenCheckMark">
          <Checkmark />
        </div>
        <Label
          datatestid={`youre-prequalified-tooltip`}
          type="Subtitle-2"
          color="fontGreen300"
        >
          You're Pre-Qualified!
        </Label>
        <div className="margR4" />
        <QuestionCircleOutlined style={{ display: 'inline-block' }} />
      </div>
    </CSTooltip>
  );
};

const ApplyNowButton = props => {
  const { loading, buttonLabel, buttonCTALabel, offerMetadata } = props;

  return (
    <div className="button-container margT16">
      <Prepopulate
        datatestid={'prepopulate'}
        prePopulatedTooltipText={offerMetadata?.prePopulatedTooltipText}
        {...props}
      />

      {props.showApplyNow && (
        <Button
          mrphType="primary"
          onClick={props.clickApplyHandler}
          datatestid="ccApplyNowButton"
          loading={loading}
        >
          <FlexContainer className="justify-center align-items-center">
            <LockIcon className="lockIcon pos-rel" />
            <Label
              datatestid={`${props.datatestid}-applyNowButtonText`}
              type="Subtitle-2"
              color="primary_cta_text_light"
            >
              {buttonCTALabel ?? buttonLabel}
            </Label>
          </FlexContainer>
        </Button>
      )}

      <div className="fclear pad4"></div>
    </div>
  );
};

const RecommendedHighlitghtBorder = props => {
  const { showHighlightFirstCard } = props;
  if (showHighlightFirstCard) {
    return (
      <FlexContainer className="bgBlue3 borderTopRadius6 justify-center">
        <Label className="bold fontWhite">RECOMMENDED</Label>
      </FlexContainer>
    );
  } else {
    return null;
  }
};

const CardOffer = props => {
  const {
    buttonCTALabel,
    isHighlightFirstCard,
    redirectClickApplyToClickDetails,
    modulePosition,
    position,
    buttonLabel,
    hideCreditLimitByConfig,
    observedApprovalOdds,
    offerMetadata,
    offerImageLinkUrlProxy,
  } = props;

  const showAmexWithConfidence = isApplyToConfidence(offerMetadata?.productKey);

  const isGuaranteeApprovalOdds = observedApprovalOdds === GUARANTEE;
  const hideCreditLimit =
    offerMetadata?.hideCreditLimit || hideCreditLimitByConfig;
  const showCreditSavings = props.extraAttributes
    ? props.extraAttributes.showCreditSavings
    : false;

  const loading = props.loading;

  const isCapitalOne =
    offerMetadata?.name.includes('Capital One') ||
    offerMetadata?.issuer === 'Capital One';

  const showBadge =
    offerMetadata?.badgeEnabledFlag && offerMetadata?.badgeLabel;

  let creditCardTopperProps = {
    offerMetadata: offerMetadata,
    hideCreditLimit: hideCreditLimit,
    showCreditSavings: showCreditSavings,
  };

  if (props.showCheckbox) {
    creditCardTopperProps = {
      ...creditCardTopperProps,
      showCheckbox: props.showCheckbox,
      cardsSelected: props.cardsSelected,
      handleCheckboxClick: props.handleCheckboxClick,
    };
  }

  let checkedClass;
  if (props?.cardsSelected) {
    checkedClass = props?.cardsSelected?.find(item => {
      return item.productKey === offerMetadata?.productKey;
    });
  }

  const showHighlightFirstCard =
    isHighlightFirstCard && position === 1 && modulePosition === 1;

  const cardOffer = (
    <div
      className={classnames('card-offer-credit-card text-left', {
        borderedCheckedContainer: checkedClass,
        'borderBlue100 borderWidth2': showHighlightFirstCard,
      })}
    >
      <RecommendedHighlitghtBorder
        showHighlightFirstCard={showHighlightFirstCard}
      />
      <div
        id="mobileOffer"
        className={classnames('pad10', 'card-offer-credit-card-content')}
      >
        <div
          className={classnames('predictedBlock', {
            'previewContainer checkbox flex-container pos-rel align-items-center justify-around':
              props.showCheckbox,
          })}
        >
          <CreditCardTopper
            {...creditCardTopperProps}
            customTopper={props.customTopper}
          />
          {props.seenPixel}
        </div>
        <div className="fclear pad4" />
        <div className="offerParent">
          <div className="format100 fclear">
            <div className="ccData m-format100">
              <div className="poof mobileshow fclear pad8" />
              <img
                alt="credit card"
                onClick={
                  redirectClickApplyToClickDetails
                    ? props.clickDetailsHandler
                    : props.clickApplyImageHandler
                }
                src={offerImageLinkUrlProxy}
                className="creditCardImage link"
                data-testid="creditCardImage"
              />
              <div className="pad8" />
              <div
                className="CardTitle otherLink bold"
                onClick={
                  redirectClickApplyToClickDetails
                    ? props.clickDetailsHandler
                    : props.clickApplyCardNameHandler
                }
                dangerouslySetInnerHTML={{ __html: offerMetadata?.name }}
                data-testid="cardTitle"
              ></div>
              {showBadge && (
                <FlexContainer
                  datatestid="creditCardOfferBadge"
                  className="justify-between items-align-center margT16"
                >
                  <Tag
                    datatestid="creditCardOfferBadge"
                    extClass="offerTag tagContainer"
                  >
                    {offerMetadata?.badgeLabel}
                  </Tag>
                </FlexContainer>
              )}
              <FlexContainer className="margT10">
                {showAmexWithConfidence && <AmexWithConfidence />}
              </FlexContainer>
              <FlexContainer className="review-ao-container justify-between margT20">
                <ReviewsRating
                  offerMetadata={offerMetadata}
                  containerCustomClass={'pointer'}
                  labelCustomClass={'fontNeutral300 text-12 margt2 m-text-left'}
                  badgeCustomClass={'pointer margt6'}
                  onClick={props.clickReviewHandler}
                />
                <ApprovalOddsBadge
                  offerMetadata={offerMetadata}
                  labelCustomClass={'fontNeutral300 text-12 margT2 text-right'}
                  badgeCustomClass={
                    isGuaranteeApprovalOdds
                      ? 'margt26 align-self-end format80'
                      : 'margT2'
                  }
                  isCarousel={true}
                />
              </FlexContainer>
              <BodyContainer {...props} />
            </div>
            <div className="poof mobileshow fclear pad2" />
          </div>
        </div>
        <TermsAndConditionsLink
          offerMetadata={props.offerMetadata}
          clickTcHandler={props.clickTcHandler}
        />
      </div>
      <div className="offerCard__footer pad10">
        {isGuaranteeApprovalOdds && <SesameGuarantee {...props} />}
        <ApplyNowButton
          loading={loading}
          buttonLabel={buttonLabel}
          buttonCTALabel={buttonCTALabel}
          isCapitalOne={isCapitalOne}
          {...props}
        />
        <div className="text-center ratesAndFees">
          <Label
            className="cardResultDetailsLink otherLink bold"
            onClick={props.clickDetailsHandler}
            datatestid="ccDetailsLink"
          >
            View more details
          </Label>
        </div>
      </div>
    </div>
  );

  if (props.customTopperOutsideContainer) {
    return (
      <div>
        <CCTopperOutsideContainer
          {...props}
          customTopperOutsideContainer={props.customTopperOutsideContainer}
        />
        {cardOffer}
      </div>
    );
  }

  return <>{cardOffer}</>;
};

const TermsAndConditionsLink = props => {
  const { offerMetadata } = props;
  if (offerMetadata?.tcUrl) {
    return (
      <Link
        className={classnames('tcBlock', props.className)}
        onClickCallback={props.clickTcHandler}
      >
        <Label className="text-12 link otherLink margT8">
          {offerMetadata?.tcText || DEFAULT_TC_TEXT}
        </Label>
      </Link>
    );
  } else {
    return <></>;
  }
};

CardOffer.propTypes = {
  offerMetadata: PropTypes.shape({
    name: PropTypes.string,
    predictedCreditLimit: PropTypes.number,
    introBtOverrideText: PropTypes.string,
    introPurchaseOverrideText: PropTypes.string,
    imageLink: PropTypes.string,
    tcUrl: PropTypes.string,
    approvalOdds: PropTypes.string,
    reviewRating: PropTypes.number,
  }),
  clickApplyHandler: PropTypes.func,
  clickReviewHandler: PropTypes.func,
  clickDetailsHandler: PropTypes.func,
  hoverApprovalOddsHandler: PropTypes.func,
};

export default CardOffer;
